import { useModal } from 'modals';

import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import { firstContentSpacing } from '../first-content-spacing';
import { ModelCta, ModelMediaSlot } from './components';
import { type ModelIntroContent } from './content';

interface ModelIntroProps {
  intro: ModelIntroContent;
  model: 'comic' | 'swing';
}

const titleClassNames = {
  comic: 'sm:max-w-[617px] xl:max-w-[480px]',
  swing: 'sm:max-w-[617px] lg:max-w-[526px] xl:max-w-[480px]',
} as const;

export default function ModelIntro({ intro, model }: ModelIntroProps) {
  const { open } = useModal();

  return (
    <section
      className={`border-primary border-b px-4 ${firstContentSpacing} sm:px-12 ${model === 'swing' ? 'pb-10 sm:pb-20' : 'pb-20'}`}
      data-model-hero={model}
    >
      <div className="flex flex-col gap-10 sm:gap-20">
        <div className="flex h-[23px] items-start">
          <Text
            as="p"
            token={{ base: '24/medium', sm: '32/medium' }}
            leading="body"
            data-model-hero-part="label"
          >
            {intro.label}
          </Text>
        </div>
        <div
          className="grid gap-12 min-[721px]:grid-cols-2 min-[721px]:gap-10 xl:grid-cols-[480px_minmax(0,1fr)] xl:gap-20 2xl:grid-cols-[minmax(0,983px)_minmax(0,1fr)]"
          data-model-hero-part="layout"
        >
          <div className="contents min-[721px]:flex min-[721px]:min-w-0 min-[721px]:flex-col min-[721px]:gap-12">
            <div
              className="order-1 flex min-w-0 flex-col gap-10 xl:gap-12"
              data-model-hero-part="title-actions"
            >
              <Text
                as="h1"
                token={{ base: '24', sm: '32' }}
                leading="body"
                className={`text-secondary min-h-[81px] break-words sm:min-h-0 ${titleClassNames[model]}`}
                data-model-hero-part="title"
              >
                {intro.title}
              </Text>
              <div
                className="grid w-full grid-cols-2 gap-4 sm:w-[312px]"
                data-model-hero-part="actions"
              >
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={() => open('ENTERPRISE_CONTACT', {})}
                >
                  Talk to us
                </Button>
                <ModelCta
                  target={intro.tryTarget}
                  label={intro.tryLabel}
                  className="w-full"
                />
              </div>
            </div>
            <div
              className="order-3 grid min-w-0 items-start gap-6"
              data-model-hero-part="body"
            >
              <Text
                as="p"
                token="24"
                leading="editorial"
                className="text-secondary whitespace-pre-wrap break-words"
                data-model-hero-part="body-primary"
              >
                {intro.paragraphs[0]}
                <br />
                <br />
                {intro.paragraphs[1]}
              </Text>
              <Text
                as="p"
                token="24"
                leading="editorial"
                className="text-secondary break-words"
                data-model-hero-part="body-secondary"
              >
                {intro.paragraphs[2]}
              </Text>
            </div>
          </div>
          <div className="order-2 min-w-0 min-[721px]:[container-type:inline-size]">
            {/* Keep the sticky element inside the stretched grid cell so it
                stops at the hero; cqw allows a negative top in short viewports. */}
            <div
              className="min-[721px]:sticky min-[721px]:top-[min(5rem,calc(100dvh-56.25cqw-1rem))]"
              data-model-hero-part="media-sticky"
            >
              <ModelMediaSlot
                media={intro.media}
                dataPart="media"
                className="aspect-video w-full min-w-0 rounded-2xl"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
